<template>
    <a-popup :visible="visible" @cancel="onCancel" :closeable="false">
        <div
            class="tw-mx-auto tw-w-[95%] md:tw-max-w-[800px] tw-h-fit tw-overflow-auto tw-flex tw-flex-col tw-border tw-border-solid dark:tw-border-gray-400 tw-border-gray-300 tw-shadow-md tw-my-16 tw-rounded">
            <div>
                <div
                    class="tw-p-4 tw-text-lg tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-solid tw-font-semibold dark:tw-text-gray-700 tw-text-gray-500 dark:tw-bg-gray-100 tw-bg-gray-50">
                    <span>{{ $t("common.sourcing_arrived") }}</span>
                    <span class="tw-text-primary-500 tw-bg-white tw-py-0 tw-rounded tw-px-2 tw-border tw-border-solid tw-border-gray-200  tw-ms-auto" >{{ sourcing.id }}</span>
                </div>

                <div class="tw-p-5 tw-bg-white tw-flex-1">
                    <div class="tw-grid tw-grid-cols-12 tw-gap-x-4 tw-gap-y-0">
                        <div class="tw-col-span-12 md:tw-col-span-12 tw-pb-4">
                            <h1 class="tw-ps-4 tw-border-s-2 tw-border-solid tw-border-primary-500">
                                {{ $t('common.sourcing_type') }}: 
                                <span class="tw-text-primary-500">{{ $t('sourcing.types.' + sourcing.sourcing_type) }}</span>
                            </h1>
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-12 tw-pb-4">
                            <h1 class="tw-ps-4 tw-border-s-2 tw-border-solid tw-border-primary-500">{{
                                $t('common.sourcing_informations') }}</h1>
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField v-model="sourcing.product_name" :disabled="sourcing.sourcing_type === 'restock'" :label="$t('fields.product_name')" type="text"
                                :error="errors.product_name && $t('errors.' + errors.product_name, {
                                    field: $t('fields.product_name'),
                                })" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6" v-if="!hasVariants">
                            <FormField v-model.number="sourcing.quantity" :label="$t('fields.quantity')" type="number" :error="errors.quantity && $t('errors.' + errors.quantity, {
                                field: $t('fields.quantity'),
                            })" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormVueSelect v-model="sourcing.destination_country" max-height="200" option-label="name"
                                option-value="value" :options="countries" :label="$t('fields.destination_country')"
                                type="text" :error="errors.destination_country && $t('errors.' + errors.destination_country, {
                                    field: $t('fields.destination_country'),
                                })">
                                <template v-slot:option="{ option }">
                                    <div>
                                        <p class="tw-max-w-[135px] tw-truncate tw-text-xs">{{ option.name }}</p>
                                    </div>
                                </template>
                            </FormVueSelect>
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormVueSelect v-model="sourcing.shipping_method" max-height="200" option-label="name"
                                option-value="value" :options="shipping_methods" :label="$t('fields.shipping_method')"
                                type="text" :error="errors.shipping_method && $t('errors.' + errors.shipping_method, {
                                    field: $t('fields.shipping_method'),
                                })">
                                <template v-slot:option="{ option }">
                                    <div>
                                        <p class="tw-max-w-[135px] tw-truncate tw-text-xs">{{ option.name }}</p>
                                    </div>
                                </template>
                            </FormVueSelect>
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-12">
                            <FormField left-icon="solar:link-bold-duotone" v-model="sourcing.product_url"
                                :label="$t('fields.product_url')" type="text" :error="errors.product_url && $t('errors.' + errors.product_url, {
                                    field: $t('fields.product_url'),
                                })" />
                        </div>

                        <div v-if="false" class="tw-col-span-12 md:tw-col-span-12">
                            <SourcingStatus v-model:sourcing="sourcing" :errors="errors" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField left-icon="solar:tag-price-bold-duotone" v-model.number="sourcing.cost_per_unit"
                                :label="$t('fields.cost_per_unit')" type="number" :error="errors.cost_per_unit && $t('errors.' + errors.cost_per_unit, {
                                    field: $t('fields.cost_per_unit'),
                                })" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField left-icon="ic:twotone-local-shipping" v-model.number="sourcing.shipping_cost"
                                :label="$t('fields.shipping_cost')" type="number" :error="errors.shipping_cost && $t('errors.' + errors.shipping_cost, {
                                    field: $t('fields.shipping_cost'),
                                })" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField left-icon="hugeicons:money-add-02" v-model.number="sourcing.additional_fees"
                                :label="$t('fields.additional_fees')" type="number" :error="errors.additional_fees && $t('errors.' + errors.additional_fees, {
                                    field: $t('fields.additional_fees'),
                                })" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField left-icon="material-symbols:weight-outline" v-model.number="sourcing.weight"
                                :label="$t('fields.weight') + ' (KG)'" type="number" :error="errors.weight && $t('errors.' + errors.weight, {
                                    field: $t('fields.weight'),
                                })" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField disabled left-icon="solar:tag-price-bold-duotone" v-model="buyingPrice" :label="$t('fields.buying-price')" type="number"
                                :error="errors.buying_price && $t('errors.' + errors.buying_price, {
                                    field: $t('fields.buying-price'),
                                })" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField left-icon="solar:tag-price-bold-duotone" v-model.number="sourcing.selling_price" :label="$t('fields.selling-price')" type="number"
                                :error="errors.selling_price && $t('errors.' + errors.selling_price, {
                                    field: $t('fields.selling-price'),
                                })" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-12">
                            <FormField v-model="sourcing.note" :label="$t('fields.note')" type="textarea" :error="errors.note && $t('errors.' + errors.note, {
                                field: $t('fields.note'),
                            })" />
                        </div>

                        <div v-if="hasVariants" class="tw-col-span-12 md:tw-col-span-12 tw-pb-4">
                            <h1 class="tw-ps-4 tw-border-s-2 tw-border-solid tw-border-primary-500">{{
                                $t('common.sourcing_variants') }}</h1>
                        </div>

                        <div v-if="hasVariants && sourcing.sourcing_type === 'new_product'" class="tw-col-span-12 md:tw-col-span-12">
                            <SourcingVariants v-model:variants="sourcing.variants" />
                        </div>

                        <div v-if="sourcing.sourcing_type === 'restock'" class="tw-col-span-12 md:tw-col-span-12">
                            <div v-if="!hasVariants" class="tw-bg-amber-50 tw-p-3 tw-rounded tw-mb-3 tw-text-amber-700 tw-text-sm">
                                {{ $t('common.no_variants_for_restock') }}
                            </div>
                            <div v-else class="tw-rounded-lg tw-overflow-hidden tw-border tw-border-gray-200">
                                <table class="tw-w-full tw-border-collapse tw-table-auto">
                                    <thead class="tw-bg-gray-50">
                                        <tr>
                                            <th class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200 tw-text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase">
                                                {{ $t('fields.variant_name') }}
                                            </th>
                                            <th class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200 tw-text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-w-24">
                                                {{ $t('fields.quantity') }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(variant, index) in sourcing.variants" :key="variant.id || index">
                                            <td class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200">
                                                <span>{{ variant.variant_name }}</span>
                                            </td>
                                            <td class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200">
                                                <FormField 
                                                    v-model.number="variant.quantity" 
                                                    type="number" 
                                                    hideLabel 
                                                    hideErrors
                                                    class="tw-w-full quantity-input"
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    class="tw-p-4 tw-text-lg tw-border-t tw-justify-end tw-border-solid tw-font-medium tw-flex dark:tw-text-gray-700 tw-text-gray-500 tw-items-center tw-gap-4 dark:tw-bg-gray-100 tw-bg-gray-50">
                    <a-button :disabled="loading" @click="onCancel" class="tw-min-w-[120px]" intent="simple">{{
                        $t("buttons.cancel") }}</a-button>
                    <a-button :disabled="loading" :loading="loading" @click="onUpdate" class="tw-min-w-[120px]">{{
                        $t("buttons.confirm") }}</a-button>
                </div>
            </div>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch, computed } from 'vue';
import { clone } from '@/utils/methods';
import { webhookArrived } from '@/api/sourcings';
import { useI18n } from 'vue-i18n';
import useAlert from '@/composables/useAlert';
import FormField from "@/components/form/FormField";
import FormVueSelect from "@/components/form/FormVueSelect";
import SourcingStatus from './partials/SourcingStatus.vue';
import SourcingVariants from './partials/SourcingVariants.vue';
import { countries as shipping_countries, shipping_methods as shipping_ways } from '@/config/sourcing';
import { useRouter } from 'vue-router';

const router = useRouter();
const i18n = useI18n()

const emit = defineEmits(['update:visible', 'update']);
const props = defineProps({
    item: {
        required: true
    },
    visible: {
        required: true,
        default: false
    }
});

const sourcing = ref(clone(props.item));
const originalSourcing = ref(clone(props.item));
const countries = computed(() => shipping_countries.map(c => ({ ...c, name: i18n.t('countries.' + c.value) })))
const shipping_methods = computed(() => shipping_ways.map(c => ({ ...c, name: i18n.t('sourcing.shipping_methods.' + c.value) })))
const errors = ref({})
const loading = ref(false)

// Check if the sourcing has variants
const hasVariants = computed(() => {
    return sourcing.value.variants && Array.isArray(sourcing.value.variants) && sourcing.value.variants.length > 0;
});

// Auto-calculate buying price whenever relevant fields change
const buyingPrice = computed(() => {
    if (!(sourcing.value.shipping_cost > 0 && sourcing.value.quantity > 0)) {
        return sourcing.value.cost_per_unit;
    }
    const costPerUnit = sourcing.value.cost_per_unit ?? 0;
    const shippingAndFees = (sourcing.value.shipping_cost + (sourcing.value.additional_fees || 0)) / sourcing.value.quantity;
    return Number(costPerUnit + shippingAndFees).toFixed(2);
});

// Watch for changes in fields that affect buying price
watch(
    () => [sourcing.value.cost_per_unit, sourcing.value.shipping_cost, sourcing.value.quantity, sourcing.value.additional_fees],
    () => {
        sourcing.value.buying_price = buyingPrice.value;
    }
);

watch(() => props.item, (newValue) => {
    sourcing.value = clone(newValue)
    originalSourcing.value = clone(newValue)
    
    // Ensure variants are properly initialized
    if (!sourcing.value.variants || !Array.isArray(sourcing.value.variants)) {
        sourcing.value.variants = [];
        console.log("Initialized variants array");
    } else {
        console.log(`Sourcing has ${sourcing.value.variants.length} variants`);
    }
}, { deep: true })

// Watch for changes in variant quantities to update total quantity
watch(() => sourcing.value.variants, () => {
    if (hasVariants.value) {
        // Calculate total quantity from variants
        sourcing.value.quantity = sourcing.value.variants.reduce((total, variant) => {
            return total + (parseInt(variant.quantity) || 0);
        }, 0);
    }
}, { deep: true });

const validateWebhookData = () => {
    let requiredFields = [];

    if(sourcing.value.sourcing_type === 'restock') {
        requiredFields = [
            //'product_name',
                //'selling_price',
                //'weight',
                //'destination_country',
                //'shipping_method',
            //'shipping_cost',
            //'cost_per_unit'
        ];
    } else {
        requiredFields = [
            'product_name',
            'selling_price',
            //'weight',
            //'destination_country',
            //'shipping_method',
        ];
    }

    // Only add quantity to required fields if there are no variants
    if (!hasVariants.value) {
        requiredFields.push('quantity');
    }
    
    const missingFields = requiredFields.filter(field => !sourcing.value[field]);
    
    if (missingFields.length > 0) {
        const missingFieldNames = missingFields.map(field => i18n.t(`fields.${field}`)).join(', ');
        useAlert(i18n.t('errors.required_fields_missing', { fields: missingFieldNames }), 'warning');
        
        errors.value = {
            ...errors.value,
            ...missingFields.reduce((acc, field) => ({
                ...acc,
                [field]: 'required'
            }), {})
        };
        return false;
    }

    // Validate variants if this sourcing has variants
    if (hasVariants.value) {
        const hasInvalidVariants = sourcing.value.variants.some(variant => 
            !variant.variant_name || !variant.quantity || variant.quantity <= 0
        );
        
        if (hasInvalidVariants) {
            useAlert(i18n.t('errors.invalid_variants'), 'warning');
            return false;
        }
    }

    return true;
}

const onUpdate = async () => {
    if (!validateWebhookData()) {
        return;
    }

    // For product with variants, calculate total quantity
    if (hasVariants.value) {
        sourcing.value.quantity = sourcing.value.variants.reduce((total, variant) => {
            return total + (parseInt(variant.quantity) || 0);
        }, 0);
    }

    const data = { 
        ...sourcing.value,
        buying_price: buyingPrice.value,
        status: 'arrived'
    }

    loading.value = true
    await webhookArrived(data)
        .then(
            res => {
                console.log('res.data: ', res.data)
                if (res.data.code === "SUCCESS") {
                    emit('update:visible', false)
                    emit('update', res.data.data.sourcing)
                    useAlert(i18n.t('messages.sourcing_arrived_success'), 'success')
                    
                    // Redirect to product update page if product data is available
                    if (res.data.data?.product?.id) {
                        router.push(`/admin/products/${res.data.data.product.id}/edit`);
                    } else {
                        useAlert(i18n.t('errors.product_not_found'), 'warning');
                    }
                }
            },
            (err) => {
                errors.value = {}
                if (err?.response?.status == 422) {
                    for (let e in err.response.data.errors) {
                        errors.value[e] = err.response.data.errors[e][0];
                    }
                    useAlert(i18n.t('errors.validation_error'), 'error');
                } else if (err?.response?.status == 401) {
                    useAlert(i18n.t('errors.unauthorized'), 'error');
                } else if (err?.response?.status == 500) {
                    useAlert(i18n.t('errors.server_error'), 'error');
                }
            }
        )
    loading.value = false
}

const onCancel = () => {
    emit('update:visible', false)
}
</script>

<style></style>